import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getAccount } from '@/api';
import {
  MIN_CASHOUT_OFFER,
  PARTIAL_CASHOUT_MAX_PERCENT,
  PARTIAL_CASHOUT_MIN_PERCENT,
  PARTIAL_CASHOUT_STEP,
  mergeCashoutHistory,
  partialCashoutAmounts,
  totalCashoutHistoryWin,
  type CashoutHistoryEntry,
} from '@/domain/cashout';
import { formatOutcomeLabel } from '@/domain/event-details';
import { formatOddForDisplay } from '@/domain/odds-format';
import { formatAmount } from '@/domain/player-balance';
import {
  isKreditBetChildTicket,
  isKreditBetMainTicket,
} from '@/domain/kredit-bet';
import type { TicketBetLiveDisplay } from '@/domain/my-bets-live';
import {
  TICKET_STATUS_LABEL,
  TICKET_STATUS_TONE,
  TicketStatus,
  betSettlement,
  canReuseTicket,
  ticketShowsRealizedWin,
  ticketTypeLabel,
  type TicketBetItem,
  type TicketListItem,
  type TicketStatus as TicketStatusValue,
} from '@/domain/tickets';
import { stripMarketPlaceholders, translateMarketLabel } from '@/i18n/market-labels';
import { translateOutcomeLabel } from '@/i18n/outcome-labels';
import { formatApiDateTime } from '@/lib/date';
import { cn } from '@/lib/cn';
import { Badge } from '../common/badge';
import { Icon } from '../common/icon';
import { Spinner } from '../common/spinner';
import { LiveClockText } from '../live/LiveClockText';
import { CashoutControl, type CashoutControlState } from './CashoutControl';
import { PartialCashoutButton } from './PartialCashoutButton';
import { TicketBetStatsWidget } from './TicketBetStatsWidget';

const CASHOUT_CONFIRM_TIMEOUT_MS = 15_000;

function asTicketStatus(status: TicketStatusValue | number): TicketStatusValue {
  if (
    status === TicketStatus.Open ||
    status === TicketStatus.Won ||
    status === TicketStatus.Lost ||
    status === TicketStatus.Cancelled ||
    status === TicketStatus.Sold ||
    status === TicketStatus.Saved ||
    status === TicketStatus.Booked
  ) {
    return status;
  }
  return TicketStatus.Open;
}

export interface MyBetTicketCardCashoutProps {
  offer: number;
  updating?: boolean;
  allowPartial?: boolean;
  history?: CashoutHistoryEntry[];
  executing?: boolean;
  onCashout?: (args: { cashoutValue: number; partialPercent?: number }) => void;
  onAcknowledgeUpdating?: () => void;
}

/** Live status / inline stats wiring from `useMyBetsLiveStatus`. */
export interface MyBetTicketCardLiveStatus {
  resolveBetLive: (bet: TicketBetItem) => TicketBetLiveDisplay;
  isStatsOpen: (matchId: number) => boolean;
  toggleStats: (matchId: number) => void;
  onTicketExpanded: (bets: readonly TicketBetItem[] | null | undefined) => void;
}

export interface MyBetTicketCardProps {
  ticket: TicketListItem;
  currency?: string;
  onReuse?: (ticketId: string) => void;
  reusePending?: boolean;
  cashout?: MyBetTicketCardCashoutProps | null;
  liveStatus?: MyBetTicketCardLiveStatus | null;
  /**
   * KreditBet creditor picker: replaces the status badge with a Select CTA and
   * hides reuse / cashout chrome so the card matches My Bets visually.
   */
  onSelect?: () => void;
  className?: string;
}

function withCurrencyPrefix(amount: string, currency: string): string {
  return currency ? `${currency}${amount}` : amount;
}

/** Figma StatusDot: open = secondary ring; won = status-success; lost = status-danger. */
function StatusDot({ settlement }: { settlement: ReturnType<typeof betSettlement> }) {
  return (
    <span
      aria-hidden
      className={cn(
        'size-3.5 shrink-0 rounded-full',
        settlement === 'open' && 'border-2 border-text-secondary bg-transparent',
        settlement === 'won' && 'bg-status-success',
        settlement === 'lost' && 'bg-status-danger',
      )}
    />
  );
}

function matchLabel(bet: TicketBetItem): string {
  const home = bet.Home?.trim() ?? '';
  const away = bet.Away?.trim() ?? '';
  if (home && away) return `${home} - ${away}`;
  return home || away || bet.Tournament?.trim() || '';
}

type LabelResolvers = {
  market: (key: string) => string;
  outcome: (code: string, specifier?: string | null) => string;
};

function resolveBetOutcome(bet: TicketBetItem, resolve: LabelResolvers): string {
  const named = bet.OutcomeName?.trim();
  if (named) {
    return formatOutcomeLabel(named, bet.Specifier ?? undefined);
  }
  const code = (bet.OutcomeId || bet.Outcome || '').trim();
  if (!code) return '—';
  return resolve.outcome(code, bet.Specifier);
}

function resolveBetMarket(bet: TicketBetItem, resolve: LabelResolvers): string {
  const key = (bet.MarketId || bet.Market || '').trim();
  if (!key) return '—';
  // Specifier stays on the outcome line (same as open betslip).
  return resolve.market(key);
}

type ConnectorPosition = 'first' | 'middle' | 'last' | 'only';

/** Vertical rail + smaller status dot for Bet Builder nested legs (Figma Connector). */
function ConnectorDot({
  settlement,
  position,
}: {
  settlement: ReturnType<typeof betSettlement>;
  position: ConnectorPosition;
}) {
  return (
    <div className="relative flex h-10 w-3.5 shrink-0 items-center justify-center">
      {/* Rail runs full segment height; filled/surface dots paint over it. */}
      <span
        aria-hidden
        className={cn(
          'absolute left-1/2 z-0 w-px -translate-x-1/2 bg-border-subtle',
          position === 'only' && 'hidden',
          position === 'first' && 'top-1/2 bottom-0',
          position === 'middle' && 'inset-y-0',
          position === 'last' && 'top-0 bottom-1/2',
        )}
      />
      <span
        aria-hidden
        className={cn(
          'relative z-10 size-2.5 shrink-0 rounded-full',
          settlement === 'open' && 'border-2 border-text-secondary bg-bg-surface',
          settlement === 'won' && 'bg-status-success',
          settlement === 'lost' && 'bg-status-danger',
        )}
      />
    </div>
  );
}

/** Figma SelectionLeg — straight bet row with optional live status / stats. */
function BetLegRow({
  bet,
  resolve,
  locale,
  live,
  statsOpen = false,
  onToggleStats,
  liveLabel,
  finishedLabel,
  translatePeriod,
  statsLabel,
}: {
  bet: TicketBetItem;
  resolve: LabelResolvers;
  locale?: string;
  live?: TicketBetLiveDisplay | null;
  statsOpen?: boolean;
  onToggleStats?: () => void;
  liveLabel: string;
  finishedLabel: string;
  translatePeriod?: (key: string) => string;
  statsLabel: string;
}) {
  const settlement = betSettlement(bet.Won);
  const home = bet.Home?.trim() ?? '';
  const away = bet.Away?.trim() ?? '';
  const isLiveBet = Boolean(live?.isLiveBet);
  const eventDate =
    !isLiveBet && bet.Date ? formatApiDateTime(bet.Date, locale) : null;
  const showLiveChrome = isLiveBet;
  const showScore =
    Boolean(live?.showResult) &&
    (live?.homeScore !== undefined || live?.awayScore !== undefined);
  const clockText = live?.finished ? finishedLabel : live?.clock;
  const showClock =
    showLiveChrome && (Boolean(clockText) || Boolean(live?.liveClockState));

  return (
    <li className="flex w-full flex-col gap-1.5 py-2.5">
      <div className="flex w-full items-start gap-2.5">
        <StatusDot settlement={settlement} />
        <div className="flex min-w-0 flex-1 flex-col gap-0.5">
          <div className="flex min-w-0 flex-wrap items-baseline gap-1.5">
            <p className="truncate text-[13px] leading-4 font-bold text-text-primary">
              {resolveBetOutcome(bet, resolve)}
            </p>
            <p className="truncate text-[12px] leading-4 text-text-secondary">
              {resolveBetMarket(bet, resolve)}
            </p>
          </div>
          {home ? (
            <p className="truncate text-[13px] font-bold text-text-primary">
              {home}
              {live?.homeRed ? (
                <span
                  className="ml-1 inline-block h-2.5 w-1.5 rotate-12 rounded-[1px] bg-status-danger align-middle"
                  aria-hidden
                />
              ) : null}
            </p>
          ) : null}
          {away ? (
            <p className="truncate text-[13px] font-bold text-text-primary">
              {away}
              {live?.awayRed ? (
                <span
                  className="ml-1 inline-block h-2.5 w-1.5 rotate-12 rounded-[1px] bg-status-danger align-middle"
                  aria-hidden
                />
              ) : null}
            </p>
          ) : null}
          {showLiveChrome ? (
            <div className="flex min-w-0 flex-wrap items-center gap-2 pt-0.5">
              {live?.showLiveBadge ? <Badge tone="live">{liveLabel}</Badge> : null}
              {showClock ? (
                <LiveClockText
                  liveClockState={live?.finished ? undefined : live?.liveClockState}
                  clock={clockText}
                  translatePeriod={translatePeriod}
                  className="text-[11px] text-text-secondary"
                />
              ) : null}
              {showScore ? (
                <span className="text-[12px] font-bold tabular-nums text-text-primary">
                  {live?.homeScore ?? '—'}:{live?.awayScore ?? '—'}
                </span>
              ) : null}
              {live?.betScoreLabel ? (
                <span className="text-[11px] tabular-nums text-status-warning">
                  {live.betScoreLabel}
                </span>
              ) : null}
            </div>
          ) : null}
          {eventDate ? (
            <p className="truncate text-[11px] text-text-secondary">{eventDate}</p>
          ) : null}
        </div>
        <div className="flex shrink-0 flex-col items-end gap-1">
          <span className="text-[13px] font-bold tabular-nums text-text-primary">
            {formatOddForDisplay(bet.Value ?? 0)}
          </span>
          {live?.showStatsButton && onToggleStats ? (
            <button
              type="button"
              aria-label={statsLabel}
              aria-pressed={statsOpen}
              onClick={(event) => {
                event.stopPropagation();
                onToggleStats();
              }}
              className={cn(
                'flex size-6 items-center justify-center rounded-sm text-icon-muted',
                statsOpen && 'text-action-secondary',
              )}
            >
              <Icon name="stats" size={14} />
            </button>
          ) : null}
        </div>
      </div>
      {statsOpen ? (
        // Indent under the leg text (past StatusDot), padding keeps width inside the card.
        <div className="w-full min-w-0 max-w-full pl-[22px]">
          <TicketBetStatsWidget
            statistics={live?.statistics ?? null}
            homeScore={live?.homeScore}
            awayScore={live?.awayScore}
          />
        </div>
      ) : null}
    </li>
  );
}

/** Figma MyBetBetBuilderGroup — BB header + nested legs on a connector rail. */
function CustomBetBlock({
  bet,
  resolve,
  bbLabel,
}: {
  bet: TicketBetItem;
  resolve: LabelResolvers;
  bbLabel: string;
}) {
  const settlement = betSettlement(bet.Won);
  const selections = bet.Selections ?? [];

  return (
    <li className="flex w-full flex-col gap-1">
      <div className="flex w-full items-center gap-2.5 py-2">
        <StatusDot settlement={settlement} />
        <Icon
          name="betbuilder"
          size={24}
          className="shrink-0 text-text-primary"
          label={bbLabel}
        />
        <p className="min-w-0 flex-1 truncate text-[13px] font-bold text-text-primary">
          {matchLabel(bet)}
        </p>
        <span className="shrink-0 text-[13px] font-bold tabular-nums text-text-primary">
          {formatOddForDisplay(bet.Value ?? 0)}
        </span>
      </div>
      {selections.length > 0 ? (
        <ul className="flex w-full flex-col">
          {selections.map((selection, index) => {
            const selSettlement = betSettlement(selection.Won);
            const position: ConnectorPosition =
              selections.length === 1
                ? 'only'
                : index === 0
                  ? 'first'
                  : index === selections.length - 1
                    ? 'last'
                    : 'middle';
            return (
              <li
                key={`sel-${selection.MatchId ?? 0}-${selection.MarketId ?? ''}-${selection.OutcomeId ?? index}-${index}`}
                className="flex w-full items-center gap-2.5"
              >
                <ConnectorDot settlement={selSettlement} position={position} />
                <div className="flex min-w-0 flex-1 flex-col justify-center gap-0.5 py-0.5">
                  <p className="truncate text-[13px] leading-4 font-bold text-text-primary">
                    {resolveBetOutcome(selection, resolve)}
                  </p>
                  <p className="truncate text-[12px] leading-4 text-text-secondary">
                    {resolveBetMarket(selection, resolve)}
                  </p>
                </div>
              </li>
            );
          })}
        </ul>
      ) : null}
    </li>
  );
}

/**
 * Collapsed My bets ticket card (Figma MyBetTicketCard) with CashoutControl row.
 * Translates market/outcome codes and expands Bet Builder `Selections`.
 */
export function MyBetTicketCard({
  ticket,
  currency = '',
  onReuse,
  reusePending = false,
  cashout = null,
  liveStatus = null,
  onSelect,
  className,
}: MyBetTicketCardProps) {
  const { t, i18n } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const [metaExpanded, setMetaExpanded] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [partialOpen, setPartialOpen] = useState(false);
  const [partialPercent, setPartialPercent] = useState(50);
  const bets = ticket.Bets ?? [];
  const status = asTicketStatus(ticket.Status);
  const statusLabel = TICKET_STATUS_LABEL[status];
  const showWin = ticketShowsRealizedWin(status);
  const winValue = showWin ? (ticket.Win ?? 0) : ticket.PossibleWin;
  const cashoutWin =
    typeof ticket.CashoutWin === 'number' &&
    Number.isFinite(ticket.CashoutWin) &&
    ticket.CashoutWin > 0
      ? ticket.CashoutWin
      : null;
  const canReuse = canReuseTicket(status);
  const feedType = getAccount()?.FeedType;
  const language = i18n.resolvedLanguage ?? i18n.language;

  const selectMode = typeof onSelect === 'function';
  const offer = cashout?.offer ?? 0;
  const showCashout = !selectMode && cashout != null;
  const fullOfferReady = offer >= MIN_CASHOUT_OFFER;
  const historyEntries = useMemo(
    () => mergeCashoutHistory(ticket, cashout?.history),
    [ticket, cashout?.history],
  );
  const showHistory = historyEntries.length > 0;
  const historyTotalWin = totalCashoutHistoryWin(historyEntries);
  const initialStake = ticket.InitialStake ?? ticket.Stake;
  const initialPossibleWin = ticket.InitialPossibleWin ?? ticket.PossibleWin;
  const historyStakeUsed = historyEntries.reduce((sum, row) => sum + row.stake, 0);
  const historyPercent =
    initialStake > 0
      ? Math.min(
          100,
          Math.round((historyStakeUsed / initialStake) * 100 + Number.EPSILON),
        )
      : 0;
  const partialAmounts = partialCashoutAmounts(offer, ticket.Stake, partialPercent);
  const activeCashoutValue = partialOpen ? partialAmounts.partialWin : offer;

  useEffect(() => {
    if (!confirming) return;
    const timer = window.setTimeout(() => setConfirming(false), CASHOUT_CONFIRM_TIMEOUT_MS);
    return () => window.clearTimeout(timer);
  }, [confirming, activeCashoutValue]);

  useEffect(() => {
    if (!cashout?.updating) return;
    const timer = window.setTimeout(() => {
      cashout.onAcknowledgeUpdating?.();
    }, 600);
    return () => window.clearTimeout(timer);
  }, [cashout, cashout?.updating]);

  useEffect(() => {
    // Live offer moved — leave Confirm so the user sees the new amount.
    setConfirming(false);
  }, [offer]);

  useEffect(() => {
    if (!fullOfferReady) setPartialOpen(false);
  }, [fullOfferReady]);

  let cashoutState: CashoutControlState = 'unavailable';
  if (cashout?.executing || cashout?.updating) {
    cashoutState = 'pending';
  } else if (!fullOfferReady) {
    cashoutState = 'unavailable';
  } else if (confirming) {
    cashoutState = 'confirm';
  } else {
    cashoutState = 'available';
  }

  const handleCashoutClick = () => {
    if (!cashout || cashout.executing || cashout.updating || !fullOfferReady) return;
    if (!confirming) {
      setConfirming(true);
      return;
    }
    setConfirming(false);
    cashout.onCashout?.({
      cashoutValue: activeCashoutValue,
      partialPercent: partialOpen ? partialPercent : undefined,
    });
  };

  const resolve = useMemo<LabelResolvers>(() => {
    void language;
    return {
      market: (key: string) =>
        stripMarketPlaceholders(translateMarketLabel(key, i18n, feedType)),
      outcome: (code: string, specifier?: string | null) =>
        formatOutcomeLabel(
          translateOutcomeLabel(code, i18n, feedType),
          specifier ?? undefined,
        ),
    };
  }, [feedType, i18n, language]);

  return (
    <article
      className={cn(
        'flex w-full shrink-0 flex-col gap-2 rounded-md bg-bg-surface px-3 pt-3 pb-2',
        className,
      )}
    >
      <button
        type="button"
        className="flex w-full items-start justify-between gap-2 text-left"
        aria-expanded={expanded}
        onClick={() => {
          setExpanded((value) => {
            const next = !value;
            if (next) liveStatus?.onTicketExpanded(ticket.Bets);
            return next;
          });
        }}
      >
        <div className="flex min-w-0 flex-1 flex-col items-start">
          <span className="flex items-center gap-2">
            <Icon
              name={expanded ? 'chevron-down' : 'chevron-up'}
              size={10}
              className={cn('shrink-0 text-icon-muted', !expanded && 'rotate-90')}
            />
            <span className="truncate text-sm font-bold text-text-primary">
              {ticketTypeLabel(ticket)}
            </span>
          </span>
          {bets.length > 0 ? (
            <span className="flex items-center gap-1.5 pt-1.5 pl-[18px]">
              {bets.map((bet, index) => (
                <StatusDot
                  key={`${ticket.Id}-dot-${bet.MatchId ?? index}-${index}`}
                  settlement={betSettlement(bet.Won)}
                />
              ))}
            </span>
          ) : null}
        </div>
        {selectMode ? (
          <button
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              onSelect();
            }}
            className="shrink-0 rounded-full bg-action-primary px-3.5 py-1.5 text-body-s font-bold text-text-on-action hover:opacity-90"
          >
            {t('Select', { defaultValue: 'Select' })}
          </button>
        ) : (
          /* Figma StatusBadge: pill, 10px label, px-8 py-2 (tighter than Badge SM). */
          <Badge
            tone={TICKET_STATUS_TONE[status]}
            size="sm"
            className="shrink-0 px-2 py-0.5 text-[10px] leading-none tracking-normal normal-case"
          >
            {t(statusLabel, { defaultValue: statusLabel })}
          </Badge>
        )}
      </button>

      <div className="flex w-full items-start justify-between whitespace-nowrap">
        <div className="flex min-w-0 flex-1 flex-col items-start gap-0.5">
          <span className="text-[11px] text-text-secondary">
            {t('Stake', { defaultValue: 'Stake' })}
          </span>
          <span className="text-sm font-bold text-text-primary">
            {withCurrencyPrefix(formatAmount(ticket.Stake), currency)}
          </span>
        </div>
        <div className="flex min-w-0 flex-1 flex-col items-center gap-0.5 text-center">
          <span className="text-[11px] text-text-secondary">
            {t('Odds', { defaultValue: 'Odds' })}
          </span>
          <span className="text-sm font-bold text-text-primary">
            {formatOddForDisplay(ticket.Odds)}
          </span>
        </div>
        <div className="flex min-w-0 flex-1 flex-col items-end gap-0.5 text-right">
          <span className="text-[11px] text-text-secondary">
            {showWin
              ? t('Win', { defaultValue: 'Win' })
              : t('PossibleWin', { defaultValue: 'Possible Win' })}
          </span>
          <span className="text-sm font-bold text-text-primary">
            {withCurrencyPrefix(formatAmount(winValue), currency)}
          </span>
        </div>
      </div>

      {!selectMode && isKreditBetMainTicket(ticket) ? (
        <p className="flex items-center gap-1.5 text-[12px] text-text-secondary">
          <Icon name="creditbetin" size={14} className="shrink-0 text-action-primary" />
          {t('UsedForCreditBet', {
            defaultValue: 'Used for Kredit Bet, cashout no longer possible',
          })}
        </p>
      ) : null}
      {!selectMode && isKreditBetChildTicket(ticket) ? (
        <p className="flex items-center gap-1.5 text-[12px] text-text-secondary">
          <Icon name="creditbetout" size={14} className="shrink-0 text-action-primary" />
          <span>
            {t('ConnectedWithCreditbet', { defaultValue: 'Connected With KreditBet' })}
            {ticket.CreditorNumber ? (
              <span className="ms-1 font-bold text-text-primary">{ticket.CreditorNumber}</span>
            ) : null}
          </span>
        </p>
      ) : null}

      {expanded && bets.length > 0 ? (
        <ul className="flex w-full flex-col">
          {bets.map((bet, index) =>
            bet.IsCustom ? (
              <CustomBetBlock
                key={`${ticket.Id}-custom-${bet.MatchId ?? index}-${index}`}
                bet={bet}
                resolve={resolve}
                bbLabel={t('BB', { defaultValue: 'BB' })}
              />
            ) : (
              <BetLegRow
                key={`${ticket.Id}-bet-${bet.MatchId ?? index}-${index}`}
                bet={bet}
                resolve={resolve}
                locale={language}
                live={liveStatus ? liveStatus.resolveBetLive(bet) : null}
                statsOpen={
                  Boolean(
                    liveStatus &&
                      bet.MatchId != null &&
                      liveStatus.isStatsOpen(bet.MatchId),
                  )
                }
                onToggleStats={
                  liveStatus && bet.MatchId != null
                    ? () => liveStatus.toggleStats(bet.MatchId!)
                    : undefined
                }
                liveLabel={t('Live', { defaultValue: 'Live' })}
                finishedLabel={t('Finished', { defaultValue: 'Finished' })}
                translatePeriod={(key) => t(key, { defaultValue: key })}
                statsLabel={t('Statistics', { defaultValue: 'Statistics' })}
              />
            ),
          )}
        </ul>
      ) : null}

      {showCashout ? (
        <div className="flex w-full flex-col gap-2">
          <div className="flex w-full items-stretch gap-2">
            <CashoutControl
              state={cashoutState}
              amount={fullOfferReady ? activeCashoutValue : null}
              currency={currency}
              onClick={handleCashoutClick}
            />
            {cashout?.allowPartial ? (
              <PartialCashoutButton
                active={partialOpen}
                disabled={
                  cashout.executing ||
                  cashout.updating ||
                  cashoutState === 'unavailable' ||
                  cashoutState === 'pending' ||
                  !fullOfferReady
                }
                onClick={() => {
                  setPartialOpen((value) => !value);
                  setConfirming(false);
                }}
              />
            ) : null}
          </div>
          {partialOpen && cashout?.allowPartial && fullOfferReady ? (
            <div className="flex w-full flex-col gap-2 rounded-md border border-border-subtle bg-bg-elevated px-3 py-2">
              <div className="flex items-center justify-between text-[12px] text-text-secondary">
                <span>
                  {t('Stake', { defaultValue: 'Stake' })}{' '}
                  <span className="font-bold text-text-primary">
                    {withCurrencyPrefix(formatAmount(partialAmounts.stakeUsed), currency)}
                  </span>
                </span>
                <span className="font-bold text-status-success">{partialPercent}%</span>
                <span>
                  {t('Cashout', { defaultValue: 'Cash out' })}{' '}
                  <span className="font-bold text-text-primary">
                    {withCurrencyPrefix(formatAmount(partialAmounts.partialWin), currency)}
                  </span>
                </span>
              </div>
              <input
                type="range"
                min={PARTIAL_CASHOUT_MIN_PERCENT}
                max={PARTIAL_CASHOUT_MAX_PERCENT}
                step={PARTIAL_CASHOUT_STEP}
                value={partialPercent}
                onChange={(event) => {
                  setPartialPercent(Number(event.target.value));
                  setConfirming(false);
                }}
                className="w-full accent-[var(--color-status-success)]"
                aria-label={t('PartialCashout', { defaultValue: 'Partial' })}
              />
            </div>
          ) : null}
        </div>
      ) : null}

      {!selectMode && cashoutWin != null ? (
        <div className="flex w-full items-center justify-between gap-2 text-[12px]">
          <span className="text-text-secondary">
            {t('CashoutWin', { defaultValue: 'Cashout Win' })}
          </span>
          <span className="font-bold text-status-success">
            {withCurrencyPrefix(formatAmount(cashoutWin), currency)}
          </span>
        </div>
      ) : null}

      {!selectMode && showHistory ? (
        <div className="flex w-full flex-col gap-2">
          <button
            type="button"
            aria-expanded={historyOpen}
            onClick={(event) => {
              event.stopPropagation();
              setHistoryOpen((value) => !value);
            }}
            className="flex w-full items-center gap-2 text-left text-[12px] text-text-secondary"
          >
            <Icon name="cashout-history" size={16} className="shrink-0 text-icon-muted" />
            <span className="min-w-0 flex-1 font-bold text-text-primary">
              {t('CashOutHistory', { defaultValue: 'Cash Out History' })}
              {historyPercent > 0 ? (
                <span className="ml-1.5 text-text-secondary">{historyPercent}%</span>
              ) : null}
            </span>
            <span className="flex size-5 shrink-0 items-center justify-center text-icon-muted">
              <Icon
                name="chevron-down"
                size={10}
                className={cn('transition-transform', !historyOpen && '-rotate-90')}
              />
            </span>
          </button>
          {historyOpen ? (
            <div className="flex w-full flex-col gap-1.5 text-[12px]">
              <div className="flex items-center justify-between gap-2 text-text-secondary">
                <span className="font-bold">
                  {t('TotalBet', { defaultValue: 'Total Bet' })}
                </span>
                <span className="font-bold text-text-primary">
                  {withCurrencyPrefix(formatAmount(initialStake), currency)}
                </span>
              </div>
              <div className="flex items-center justify-between gap-2 text-text-secondary">
                <span className="font-bold">
                  {t('CurrentAmount', { defaultValue: 'Current Amount' })}
                </span>
                <span className="font-bold text-text-primary">
                  {withCurrencyPrefix(formatAmount(ticket.Stake), currency)}
                </span>
              </div>
              <div className="flex items-center justify-between gap-2 text-text-secondary">
                <span className="font-bold">
                  {t('PossibleWin', { defaultValue: 'Possible Win' })}
                </span>
                <span className="font-bold text-text-primary">
                  {withCurrencyPrefix(formatAmount(initialPossibleWin), currency)}
                </span>
              </div>
              <div className="flex items-center justify-between gap-2 text-text-secondary">
                <span className="font-bold">
                  {t('CurrentPossibleWin', { defaultValue: 'Current Pos. Win' })}
                </span>
                <span className="font-bold text-text-primary">
                  {withCurrencyPrefix(formatAmount(ticket.PossibleWin), currency)}
                </span>
              </div>
              <div className="flex items-center justify-between gap-2 text-text-secondary">
                <span className="font-bold">
                  {t('BetWin', { defaultValue: 'Bet Win' })}
                </span>
                <span className="font-bold text-text-primary">
                  {withCurrencyPrefix(formatAmount(historyTotalWin), currency)}
                </span>
              </div>
              <div className="mt-1 overflow-hidden rounded-md bg-bg-elevated">
                <div className="grid grid-cols-3 gap-1 px-2 py-1.5 text-[11px] font-bold text-text-secondary">
                  <span>{t('StakeUsed', { defaultValue: 'Stake Used' })}</span>
                  <span className="text-center">
                    {t('Cashout', { defaultValue: 'Cash out' })}
                  </span>
                  <span className="text-right">
                    {t('Date', { defaultValue: 'Date' })}
                  </span>
                </div>
                <ul className="flex flex-col">
                  {historyEntries.map((entry) => (
                    <li
                      key={`${entry.id ?? entry.at}-${entry.amount}`}
                      className="grid grid-cols-3 gap-1 border-t border-border-subtle px-2 py-1.5 text-[11px] text-text-primary"
                    >
                      <span className="font-bold">
                        {withCurrencyPrefix(formatAmount(entry.stake), currency)}
                      </span>
                      <span className="text-center font-bold">
                        {withCurrencyPrefix(formatAmount(entry.amount), currency)}
                      </span>
                      <span className="text-right text-text-secondary">
                        {entry.at > 0 ? formatApiDateTime(entry.at, language) : '—'}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ) : null}
        </div>
      ) : null}

      {!selectMode ? (
        <div className="flex w-full flex-col gap-1.5">
          <div className="flex w-full items-center justify-between gap-2">
            {canReuse ? (
              <button
                type="button"
                disabled={reusePending || !onReuse}
                onClick={(event) => {
                  event.stopPropagation();
                  onReuse?.(ticket.Id);
                }}
                className={cn(
                  'inline-flex items-center gap-1.5 rounded-full border border-border-default',
                  'bg-bg-elevated px-2 py-0.5 text-[10px] font-bold text-text-primary',
                  'transition-colors hover:bg-bg-surface disabled:cursor-not-allowed disabled:opacity-50',
                )}
              >
                {reusePending ? (
                  <Spinner size="sm" label={null} />
                ) : (
                  <Icon name="rebet" size={10} className="text-icon-default" />
                )}
                {t('ReuseBet', { defaultValue: 'Reuse Bet' })}
              </button>
            ) : (
              <span />
            )}
            <button
              type="button"
              aria-expanded={metaExpanded}
              aria-label={t('TicketDetails', { defaultValue: 'Ticket details' })}
              onClick={(event) => {
                event.stopPropagation();
                setMetaExpanded((value) => !value);
              }}
              className="flex size-5 shrink-0 items-center justify-center rounded text-icon-muted transition-colors hover:bg-bg-elevated hover:text-text-primary"
            >
              <Icon
                name="chevron-down"
                size={10}
                className={cn('transition-transform', !metaExpanded && '-rotate-90')}
              />
            </button>
          </div>
          {metaExpanded ? (
            <div className="flex w-full items-center justify-between text-[11px] text-text-secondary">
              <span className="shrink-0 whitespace-nowrap">
                {formatApiDateTime(ticket.Date, language)}
              </span>
              <span className="min-w-0 flex-1 truncate text-right">
                {t('TicketId', { defaultValue: 'Ticket Id' })} {ticket.Id}
              </span>
            </div>
          ) : null}
        </div>
      ) : null}
    </article>
  );
}
